/**
 * Custom GenericStackbarChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

import { GenericStackbarChart as ParentGenericStackbarChartComponent } from '@shared-lib/chart-components/generic-stackbar-chart/generic-stackbar-chart';

import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { CustomConfigService } from '../../shared-services/custom-config.service';

import * as echarts from 'echarts';
import {
  formatValueLabel,
  formatNumberWithCommas,
  calculateCategoryTotals,
  convertToPercentage,
  sortSeriesByTotal,
  buildLegendSelectedState,
} from '@shared-lib/utils/chart.utils';
import { } from '@shared-lib/utils/color.utils';
import { buildCategoryFilterData, buildLegendSyncData } from '@shared-lib/utils/filter.utils';
import { downloadAsExcel } from '@shared-lib/utils/export.utils';

@Component({
  selector: 'generic-stackbar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './generic-stackbar-chart.html',
  styleUrl: './generic-stackbar-chart.scss',
})
export class GenericStackbarChart extends ParentGenericStackbarChartComponent {
  protected override allCategories: string[] = []; // Track categories for x-axis click
  protected originalSeriesNames: string[] = []; // Track original casing for filter payloads
  protected chartSeriesData: any[] = []; // Store processed series data for dynamic totals
  protected chartLineSeriesData: any[] = []; // Store line series data for tooltip correlation
  protected currentTotals: number[] = []; // Store current dynamic totals
  protected override xAxisYearsRaw: string[] = [];
  private yearAxisIndexToYear: Record<number, string> = {};
  private readonly noDataText = 'No Data avaliable try changing filters';
  private selectedSlices: Array<{ seriesName: string; dataIndex: number }> = [];
  private rawChartData: any = null;
  private _origCategoryFields: string[] | null = null;
  private _origEnableQuarterYear: boolean | null = null;
  public drillState: 'default' | 'yearly' | 'quarterly' = 'default';
  public drilledYears: string[] = [];
  public isDrilledUp = false; // kept for CSV compat

  private sliceKey(s: { seriesName: string; dataIndex: number }): string {
    return `${s.seriesName}@@${s.dataIndex}`;
  }

  private normalizeCategorySelection(categories: string[]): string[] {
    return Array.from(
      new Set(
        (categories || [])
          .map((category) => String(category || '').trim())
          .filter(Boolean),
      ),
    );
  }

  private getCategoryForSlice(slice: {
    seriesName: string;
    dataIndex: number;
  }): string {
    const category = this.allCategories?.[slice?.dataIndex];
    return category ? String(category) : '';
  }

  private syncSelectedYearsFromCategories(): void {
    this.selectedYears = this.getSelectedYearsFromCategories();
  }

  private getSelectedYearsFromCategories(): string[] {
    return Array.from(
      new Set(
        (this.selectedCategories || [])
          .map((category) => this.extractYearFromCategoryLocal(category))
          .filter(Boolean),
      ),
    );
  }

  private isYearFullySelected(yearValue: string): boolean {
    if (!yearValue) return false;

    const categoriesForYear = (this.allCategories || []).filter(
      (category) => this.extractYearFromCategoryLocal(category) === yearValue,
    );
    if (!categoriesForYear.length) return false;

    const selectedSet = new Set(this.selectedCategories || []);
    return categoriesForYear.every((category) => selectedSet.has(category));
  }

  private reconcileSlicesWithSelectedCategories(
    clearSlicesWhenNoCategorySelection: boolean = false,
  ): void {
    const normalizedCategories = this.normalizeCategorySelection(
      this.selectedCategories || [],
    );
    this.selectedCategories = normalizedCategories;
    this.syncSelectedYearsFromCategories();

    if (normalizedCategories.length === 0) {
      if (clearSlicesWhenNoCategorySelection) {
        this.selectedSlices = [];
      }
      return;
    }

    const selectedCategorySet = new Set(normalizedCategories);
    this.selectedSlices = (this.selectedSlices || []).filter((slice) =>
      selectedCategorySet.has(this.getCategoryForSlice(slice)),
    );
  }


  private selectedYears: string[] = []; // for year-axis multi-select

  private getQuarterNum(q: string): number {
    const m = String(q || '').match(/Q(\d)/i);
    return m ? Number(m[1]) : 0;
  }

  private formatRoundedPercent(value: any): string {
    const numericValue = Number(
      typeof value === 'object' ? (value?.value ?? 0) : value,
    );
    const roundedValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
    return `${roundedValue}%`;
  }

  protected override buildCenteredAxisLabels(values: string[]): string[] {
    this.yearAxisIndexToYear = {};

    if (!values?.length) {
      return [];
    }

    const centered = new Array(values.length * 2 + 1).fill('');
    for (let start = 0; start < values.length;) {
      const value = String(values[start] ?? '').trim();
      let end = start;
      while (
        end + 1 < values.length &&
        String(values[end + 1] ?? '').trim() === value
      ) {
        end++;
      }
      if (value) {
        // Slot index space is half-steps:
        // odd => quarter center, even => boundary between two quarters.
        const midpointSlot = start + end + 1;
        centered[midpointSlot] = value;
        this.yearAxisIndexToYear[midpointSlot] = value;
      }
      start = end + 1;
    }
    return centered;
  }





  private normalizeLegendValueForSync(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private toFilterKeyArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((entry) => String(entry ?? '').trim())
        .filter(Boolean);
    }

    return String(value ?? '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  private getLegendFilterKeyCandidates(): string[] {
    const keys = new Set<string>();

    this.toFilterKeyArray(this.data?.legendFilterKey).forEach((key) => keys.add(key));
    this.toFilterKeyArray(this.data?.legendFilterkey).forEach((key) => keys.add(key));
    this.toFilterKeyArray(this.data?.legendKey).forEach((key) => keys.add(key));
    this.toFilterKeyArray(this.data?.seriesKey).forEach((key) => keys.add(key));

    this.toFilterKeyArray(this.data?.filterKey)
      .filter(
        (key) => !['year', 'quarter', 'qtr', 'category'].includes(key.toLowerCase()),
      )
      .forEach((key) => keys.add(key));

    return Array.from(keys);
  }

  private getFirstLegendFilterValue(
    keys: string[],
    requestParams: any,
    queryParams: any,
  ): any {
    for (const key of keys) {
      if (requestParams?.[key] !== undefined && requestParams?.[key] !== null) {
        return requestParams[key];
      }
      if (queryParams?.[key] !== undefined && queryParams?.[key] !== null) {
        return queryParams[key];
      }
    }
    return undefined;
  }

  private syncLegendSelectionFromFilters(): void {
    const currentFilters = this.globalFilterService.getCurrentFilters?.() || {};
    const queryParams = currentFilters?.queryParams || {};
    const requestParams = currentFilters?.requestParams || {};
    const legendKeys = this.getLegendFilterKeyCandidates();
    const rawLegendValue = this.getFirstLegendFilterValue(
      legendKeys,
      requestParams,
      queryParams,
    );

    if (rawLegendValue === undefined || rawLegendValue === null) {
      this.currentlySelectedIndices = [];
      return;
    }

    const selectedValues = (Array.isArray(rawLegendValue) ? rawLegendValue : [rawLegendValue])
      .map((value) => this.normalizeLegendValueForSync(value))
      .filter(Boolean);

    if (!selectedValues.length) {
      this.currentlySelectedIndices = [];
      return;
    }

    const matchedIndices = this.allSeriesNames
      .map((seriesName: string, index: number) => {
        const originalSeriesName = this.originalSeriesNames[index] || seriesName;
        const normalizedSeriesName = this.normalizeLegendValueForSync(seriesName);
        const normalizedOriginalSeriesName =
          this.normalizeLegendValueForSync(originalSeriesName);
        const isMatch = selectedValues.some(
          (value) =>
            value === normalizedSeriesName || value === normalizedOriginalSeriesName,
        );

        return isMatch ? index : -1;
      })
      .filter((index: number) => index !== -1);

    this.currentlySelectedIndices = Array.from(new Set(matchedIndices));
  }

  constructor(
    configService: CustomConfigService,
    globalFilterService: GlobalFilterService,
    chartFilterHandler: ChartFilterHandlerService,
  ) {
    super(configService, globalFilterService, chartFilterHandler);
  }

  // ── Drill Up: aggregate quarters → show only yearly bars ─────────────────
  public drillUp(): void {
    if (!this.rawChartData || this.drillState === 'yearly') return;
    this.drilledYears = [];
    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey;
    const valueField = this.data?.valueField || 'value';
    if (!dataKey || !Array.isArray(this.rawChartData[dataKey]) || !seriesKey) return;

    // Save original config on first drill (from default state)
    if (this.drillState === 'default') {
      this._origCategoryFields = this.data.categoryFields ? [...this.data.categoryFields] : ['quarter', 'year'];
      this._origEnableQuarterYear = this.data?.chartConfig?.enableQuarterYearAxis !== false;
    }

    // Always aggregate from the ORIGINAL raw data
    const sourceData: any[] = this.rawChartData[dataKey];
    const aggMap = new Map<string, number>();
    for (const row of sourceData) {
      const key = `${row['year']}@@${row[seriesKey]}`;
      aggMap.set(key, (aggMap.get(key) || 0) + (Number(row[valueField]) || 0));
    }
    const yearlyRows: any[] = [];
    aggMap.forEach((total, key) => {
      const [year, series] = key.split('@@');
      yearlyRows.push({ year: Number(year), [seriesKey]: series, [valueField]: total });
    });

    // Snapshot original raw data before initChart() overwrites it
    const savedRaw = this.rawChartData;

    const yearlyData = { ...this.rawChartData, [dataKey]: yearlyRows };
    this.data = { ...this.data, categoryFields: ['year'], enableQuarterYearAxis: false };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: false };
    }
    this.drillState = 'yearly';
    this.isDrilledUp = true;
    this.initChart(yearlyData);

    // Restore original raw data so drillDown/drillReset still work
    this.rawChartData = savedRaw;
    // Restore original categoryFields on data so filter-triggered reloads still work
    this.data = { ...this.data, categoryFields: this._origCategoryFields || ['quarter', 'year'] };
  }

  // ── Drill Down: aggregate years → show only quarter bars (Q1-Q4 summed) ────
  public drillDown(): void {
    if (!this.rawChartData || this.drillState === 'quarterly') return;
    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey;
    const valueField = this.data?.valueField || 'value';
    if (!dataKey || !Array.isArray(this.rawChartData[dataKey]) || !seriesKey) return;

    // Save original config on first drill (from default state)
    if (this.drillState === 'default') {
      this._origCategoryFields = this.data.categoryFields ? [...this.data.categoryFields] : ['quarter', 'year'];
      this._origEnableQuarterYear = this.data?.chartConfig?.enableQuarterYearAxis !== false;
    }

    // Always aggregate from the ORIGINAL raw data
    let sourceData: any[] = this.rawChartData[dataKey];

    // If drilling down from a selection (Yearly or Default view), filter to those years/quarters
    if (this.selectedCategories?.length > 0) {
      this.drilledYears = this.getSelectedYearsFromCategories();
      sourceData = sourceData.filter(row => {
        const rowYear = String(row['year'] || '');
        const rowQtr = this.extractQuarterFromCategoryLocal(row['quarter'] || row['qtr'] || '');

        return this.selectedCategories.some(selCat => {
          const selYear = this.extractYearFromCategoryLocal(selCat);
          const selQtr = this.extractQuarterFromCategoryLocal(selCat);

          if (selYear && selQtr) {
            return selYear === rowYear && selQtr === rowQtr;
          } else if (selYear) {
            return selYear === rowYear;
          } else if (selQtr) {
            return selQtr === rowQtr;
          }
          return false;
        });
      });
    } else {
      this.drilledYears = [];
    }

    const aggMap = new Map<string, number>();
    const quarterOrder = ['Q1', 'Q2', 'Q3', 'Q4'];
    for (const row of sourceData) {
      const qtr = (String(row['quarter'] || '').match(/Q\d/i)?.[0] || '').toUpperCase();
      if (!qtr) continue;
      const key = `${qtr}@@${row[seriesKey]}`;
      aggMap.set(key, (aggMap.get(key) || 0) + (Number(row[valueField]) || 0));
    }
    const quarterlyRows: any[] = [];
    aggMap.forEach((total, key) => {
      const [qtr, series] = key.split('@@');
      quarterlyRows.push({ quarter: qtr, [seriesKey]: series, [valueField]: total });
    });
    quarterlyRows.sort((a: any, b: any) =>
      quarterOrder.indexOf(a.quarter) - quarterOrder.indexOf(b.quarter)
    );

    // Snapshot original raw data before initChart() overwrites it
    const savedRaw = this.rawChartData;

    const quarterlyData = { ...this.rawChartData, [dataKey]: quarterlyRows };
    this.data = { ...this.data, categoryFields: ['quarter'], enableQuarterYearAxis: false };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: false };
    }
    this.drillState = 'quarterly';
    this.isDrilledUp = false;
    this.initChart(quarterlyData);

    // Restore original raw data so drillUp/drillReset still work
    this.rawChartData = savedRaw;
    // Restore original categoryFields and enableQuarterYearAxis on data so filter-triggered reloads still work
    const origQtr = this._origEnableQuarterYear !== null ? this._origEnableQuarterYear : true;
    this.data = { ...this.data, categoryFields: this._origCategoryFields || ['quarter', 'year'], enableQuarterYearAxis: origQtr };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: origQtr };
    }
  }

  // ── Drill Reset: restore original year+quarter view ────────────────────────
  public drillReset(): void {
    if (!this.rawChartData || this.drillState === 'default') return;
    this.drilledYears = [];
    const origFields = this._origCategoryFields || ['quarter', 'year'];
    const origQtr = this._origEnableQuarterYear !== null ? this._origEnableQuarterYear : true;
    this.data = { ...this.data, categoryFields: origFields, enableQuarterYearAxis: origQtr };
    if (this.data.chartConfig) {
      this.data.chartConfig = { ...this.data.chartConfig, enableQuarterYearAxis: origQtr };
    }
    this.drillState = 'default';
    this.isDrilledUp = false;
    this._origCategoryFields = null;
    this._origEnableQuarterYear = null;
    this.initChart(this.rawChartData);
    // rawChartData will be reset to itself by initChart — that is correct for the default state
  }

  protected override loadAndRenderChart(filters?: any): void {
    // If it's a global filter reset (no widgetId and no specific changed key),
    // reset the local drill state so the chart returns to its default view.
    // Note: updateAllFilters() emits WITHOUT changedFilterKey (undefined),
    // while clearAllFilters() emits with changedFilterKey: null.
    // We must handle both cases to cover all reset paths.
    const isGlobalReset = filters && !filters.widgetId &&
      (filters.changedFilterKey === null || filters.changedFilterKey === undefined);
    if (isGlobalReset) {
      this.drillState = 'default';
      // Only restore from saved drill originals when a drill was actually performed.
      // If _origEnableQuarterYear is null, no drill happened and chartConfig already
      // holds the correct original values — do NOT override with a wrong default.
      if (this._origEnableQuarterYear !== null) {
        this.data = {
          ...this.data,
          categoryFields: this._origCategoryFields || this.data?.categoryFields,
          enableQuarterYearAxis: this._origEnableQuarterYear,
        };
        if (this.data.chartConfig) {
          this.data.chartConfig = {
            ...this.data.chartConfig,
            enableQuarterYearAxis: this._origEnableQuarterYear,
          };
        }
        // Clear saved originals after restoring
        this._origCategoryFields = null;
        this._origEnableQuarterYear = null;
      }
    }
    super.loadAndRenderChart(filters);
  }

  protected override initChart(chartData: any): void {
    // Only update rawChartData when we are in default state (fresh API data).
    // During drill operations, rawChartData is managed by the drill methods themselves.
    if (this.drillState === 'default') {
      this.rawChartData = chartData;
    }
    if (!chartData) {
      console.warn('No chart data found for widget:', this.data?.widgetId);
      this.showNoDataState();
      return;
    }

    // ===== EXTRACT CONFIGURATION WITH DEFAULTS (same as current behavior) =====
    const config = this.data?.chartConfig || {};
    this.enableQuarterYearAxis = config.enableQuarterYearAxis === true;

    const showLegend = config.showLegend !== false;
    const legendPosition = config.legendPosition || 'top';
    const showDataLabels = config.showDataLabels !== false;
    const dataLabelPosition = config.dataLabelPosition || 'inside';
    const showTotalLabels = config.showTotalLabels !== false;
    const colorMap = config.colorMap || {};
    const barWidth = config.barWidth;
    const showYAxisLabels = config.showYAxisLabels !== false;
    const smoothLine = config.smoothLine === true; // Default to false (zig-zag)
    const stackType = config?.stackType || 'normal';
    const stackOrder = config?.stackOrder || 'desc'; // 'desc' = largest on top (default), 'asc' = smallest on top
    const seriesOrder: string[] = config?.seriesOrder || []; // Manual series order - if provided, overrides stackOrder

    // Position-based colors for cross-chart color consistency
    // If provided, colors are assigned by index (same index = same color across all charts)
    // `topNColorsByStackPosition` in config is documented top->bottom: [top_color, ..., bottom_color]
    // For ECharts series array [bottom_series, ..., top_series], assign top_series the top_color.
    const positionColors = config.topNColorsByStackPosition || [];
    const positionColorsTopToBottom = config.positionColorsTopToBottom === true;

    // ===== CONFIGURABLE FIELD NAMES (fully generic) =====
    const categoryFields: string[] = this.data?.categoryFields || [
      'quarter',
      'year',
    ]; // Fields to combine for category
    const categoryDelimiter: string = this.data?.categoryDelimiter ?? ' '; // Delimiter between category fields
    const valueField: string = this.data?.valueField || 'value'; // Field containing the value

    // Helper to build category string from configurable fields
    const buildCategoryString = (item: any): string => {
      return categoryFields
        .map((field) => item[field] ?? '')
        .join(categoryDelimiter);
    };

    let categories: string[] = [];
    let seriesData: any[] = [];

    // 1. GENERIC FLAT ARRAY FORMAT (Configurable Keys)
    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey;
    // console.log(`[GenericStackbar] Widget ${this.data?.widgetId} - Format check: dataKey=${dataKey}, seriesKey=${seriesKey}, hasDataKey=${!!dataKey}, hasChartDataKey=${!!(dataKey && chartData[dataKey])}, isArray=${!!(dataKey && Array.isArray(chartData[dataKey]))}`);

    if (dataKey && chartData[dataKey] && Array.isArray(chartData[dataKey])) {
      const flatData = chartData[dataKey];

      // const uniqueCategories = Array.from(
      //   new Set(flatData.map((d: any) => buildCategoryString(d))),
      // );
      // categories = uniqueCategories;

      const uniqueCategories = Array.from(
        new Set(flatData.map((d: any) => buildCategoryString(d))),
      );


      // categories = uniqueCategories.sort((a: string, b: string) => {
      //   const numA = Number(a);
      //   const numB = Number(b);

      //   if (!isNaN(numA) && !isNaN(numB)) {
      //     return numA - numB; // numeric sort (2019 → 2025)
      //   }

      //   return a.localeCompare(b); // fallback for non-numeric categories
      // });


      this.enableQuarterYearAxis = config.enableQuarterYearAxis === true;

      const parseCat = (cat: string) => {
        // assumes delimiter is space by default, e.g., "Q1 2024"
        // if you use another delimiter, this still works because we regex the year and quarter
        const year = Number((String(cat).match(/(19|20)\d{2}/)?.[0]) || 0);
        const q = (String(cat).match(/Q\d/i)?.[0]) || '';
        const qn = this.getQuarterNum(q);
        return { year, qn, q };
      };

      categories = [...uniqueCategories].sort((a: string, b: string) => {
        const A = parseCat(a);
        const B = parseCat(b);
        if (A.year !== B.year) return A.year - B.year;
        return A.qn - B.qn;
      });

      const uniqueSeriesNames = Array.from(
        new Set(flatData.map((d: any) => d[seriesKey] || 'Unknown')),
      );

      seriesData = uniqueSeriesNames.map((name: any) => {
        const seriesPoints = categories.map((cat) => {
          const point = flatData.find(
            (d: any) => d[seriesKey] === name && buildCategoryString(d) === cat,
          );
          return point ? { ...point, value: point[valueField] } : 0;
        });

        return {
          name: name,
          type: 'bar',
          stack: 'total',
          emphasis: { focus: 'none' },
          data: seriesPoints,
        };
      });
    }

    // 2. BACKWARD COMPATIBILITY: Dictionary Format
    else if (!chartData.category && !chartData.series) {
      const seriesNames = Object.keys(chartData).filter((key) => {
        const value = chartData[key];
        return (
          Array.isArray(value) &&
          value.every((item: any) => item !== null && typeof item === 'object')
        );
      });

      if (seriesNames.length > 0) {
        const firstSeriesData =
          chartData[seriesNames.find((name) => chartData[name]?.length > 0) || seriesNames[0]];
        if (Array.isArray(firstSeriesData)) {
          categories = firstSeriesData.map((item: any) =>
            buildCategoryString(item),
          );
        }
        seriesData = seriesNames.map((name) => {
          const rawData = chartData[name];
          return {
            name: name,
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'none' },
            data: Array.isArray(rawData)
              ? rawData.map((d: any) => ({ ...d, value: d[valueField] }))
              : [],
          };
        });
      }
    }

    // 3. BACKWARD COMPATIBILITY: Standard Format (category + series)
    else if (chartData.category && chartData.series) {
      categories = chartData.category;
      seriesData = chartData.series.map((s: any) => ({
        name: s.name,
        type: 'bar',
        stack: 'total',
        emphasis: { focus: 'none' },
        data: s.data,
      }));
    }

    // 4. PARSE LINE SERIES DATA (Optional)
    const lineSeriesDataKey = this.data?.lineSeriesDataKey;
    const lineSeriesKey = this.data?.lineSeriesKey;
    const lineSeriesValueField = this.data?.lineSeriesValueField || 'value';
    let lineSeriesData: any[] = [];

    console.log('[LineSeriesDebug] lineSeriesDataKey:', lineSeriesDataKey, 'chartData[lineSeriesDataKey]:', chartData[lineSeriesDataKey]);
    if (lineSeriesDataKey && chartData[lineSeriesDataKey] && Array.isArray(chartData[lineSeriesDataKey])) {
      const flatLineData = chartData[lineSeriesDataKey];
      console.log('[LineSeriesDebug] flatLineData length:', flatLineData.length, 'useSingleLineSeries:', this.data?.useSingleLineSeries);

      // Try to use existing categories, or extract from line data
      if (categories.length === 0) {
        const uniqueCategories = Array.from(
          new Set(flatLineData.map((d: any) => buildCategoryString(d))),
        );
        categories = uniqueCategories;
      }

      // Check if we should create a single line series or multiple grouped by lineSeriesKey
      const useSingleLineSeries = this.data?.useSingleLineSeries === true;

      if (useSingleLineSeries) {
        // Create a single line series taking first value per category
        const lineSeriesPoints = categories.map((cat) => {
          const point = flatLineData.find(
            (d: any) => buildCategoryString(d) === cat,
          );

          if (point) {
            return {
              ...point,
              value: point[lineSeriesValueField] ?? 0,
            };
          }

          return { value: 0 };
        });

        lineSeriesData = [
          {
            name: this.data?.lineSeriesName || 'Trend',
            type: 'line',
            smooth: smoothLine,
            yAxisIndex: 1,
            emphasis: { focus: 'none' },
            data: lineSeriesPoints,
          },
        ];
      } else {
        // Create multiple line series grouped by lineSeriesKey
        const uniqueLineSeriesNames = Array.from(
          new Set(flatLineData.map((d: any) => d[lineSeriesKey] || 'Unknown')),
        );

        lineSeriesData = uniqueLineSeriesNames.map((name: any) => {
          const seriesPoints = categories.map((cat) => {
            const point = flatLineData.find(
              (d: any) => d[lineSeriesKey] === name && buildCategoryString(d) === cat,
            );

            if (point) {
              return {
                ...point,
                value: point[lineSeriesValueField] ?? 0,
              };
            }

            return { value: 0 };
          });

          return {
            name: name,
            type: 'line',
            smooth: smoothLine,
            yAxisIndex: 1,
            emphasis: { focus: 'none' },
            data: seriesPoints,
          };
        });
      }
    }

    console.log('[LineSeriesDebug] Final lineSeriesData:', lineSeriesData);

    if (!categories.length || (!seriesData.length && !lineSeriesData.length)) {
      this.showNoDataState();
      return;
    }

    // Apply uppercase transformation to legend labels if configured
    if (config.legendUpperCase) {
      seriesData = seriesData.map(s => {
        const originalName = String(s.name);
        return {
          ...s,
          name: originalName.toUpperCase(),
          originalName: originalName // Store original casing!
        };
      });

      // Robust color mapping: If uppercase is enabled, ensure colorMap works case-insensitively
      if (Object.keys(colorMap).length > 0) {
        const uppercaseColorMap: any = {};
        Object.keys(colorMap).forEach(key => {
          uppercaseColorMap[key.toUpperCase()] = colorMap[key];
        });
        Object.assign(colorMap, uppercaseColorMap);
      }
    }

    // Sort series based on configuration:
    // 1. If seriesOrder is provided, use that exact manual order (overrides stackOrder)
    // 2. Otherwise, sort by total value using stackOrder ('desc' = largest on top, 'asc' = smallest on top)
    let sortedSeriesData: any[];

    if (seriesOrder.length > 0) {
      // Manual order provided - sort series according to seriesOrder array
      // Series not in seriesOrder will be appended at the end in their original order
      sortedSeriesData = [...seriesData].sort((a, b) => {
        const indexA = seriesOrder.indexOf(a.name);
        const indexB = seriesOrder.indexOf(b.name);

        // Both in seriesOrder: sort by their position in the array
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // Only A is in seriesOrder: A comes first
        if (indexA !== -1) return -1;
        // Only B is in seriesOrder: B comes first
        if (indexB !== -1) return 1;
        // Neither in seriesOrder: maintain original order
        return 0;
      });
    } else {
      // No manual order - use value-based sorting (existing functionality)
      sortedSeriesData = sortSeriesByTotal(
        seriesData,
        stackOrder === 'desc' ? 'asc' : 'desc',
      );
    }

    // If lastKey is configured, pin that series to the very bottom of the stack (first in array)
    const lastKey: string | undefined = this.data?.lastKey;
    if (lastKey) {
      const lastKeyLower = lastKey.toLowerCase();
      const lastIndex = sortedSeriesData.findIndex(
        (s) => (s.name || '').toLowerCase() === lastKeyLower,
      );
      if (lastIndex !== -1) {
        const [pinnedSeries] = sortedSeriesData.splice(lastIndex, 1);
        sortedSeriesData.unshift(pinnedSeries); // First in array = bottom of visual stack
      }
    }

    // CRITICAL: Store all series names for legend sync (DO NOT CHANGE)
    this.allSeriesNames = sortedSeriesData.map((s) => s.name).concat(lineSeriesData.map((s) => s.name));

    // CRITICAL: Cache original names for filtering
    this.originalSeriesNames = sortedSeriesData.map(s => s.originalName || s.name).concat(lineSeriesData.map((s) => s.name));

    // Store categories for x-axis click filtering (used for table cross-filtering)
    this.allCategories = categories;
    const parseCat = (cat: string) => {
      // assumes delimiter is space by default, e.g., "Q1 2024"
      // if you use another delimiter, this still works because we regex the year and quarter
      const year = Number((String(cat).match(/(19|20)\d{2}/)?.[0]) || 0);
      const q = (String(cat).match(/Q\d/i)?.[0]) || '';
      const qn = this.getQuarterNum(q);
      return { year, qn, q };
    };

    if (this.enableQuarterYearAxis) {
      this.xAxisQuarters = categories.map(c => parseCat(c).q || c);
      this.xAxisYearsRaw = categories.map(c => String(parseCat(c).year || ''));
      this.xAxisYears = this.buildCenteredAxisLabels(this.xAxisYearsRaw);
    } else {
      this.xAxisQuarters = [];
      this.xAxisYears = [];
      this.xAxisYearsRaw = [];
      this.yearAxisIndexToYear = {};
    }


    // Calculate initial totals for each category using utility
    this.currentTotals = calculateCategoryTotals(categories, sortedSeriesData);
    this.chartSeriesData = sortedSeriesData;
    this.chartLineSeriesData = lineSeriesData;

    // Apply percentage conversion if stackType is 'percentage' using utility
    const displaySeriesData =
      stackType === 'percentage'
        ? convertToPercentage(sortedSeriesData, this.currentTotals)
        : sortedSeriesData;
    // Build series with labels and colors
    const seriesWithLabels = displaySeriesData.map((s, index) => {


      // colorMap takes priority (allows overriding specific series like "Others")
      // then fall back to position-based colors
      const seriesColor =
        colorMap[s.name] ||
        (positionColors.length > 0
          ? positionColors[displaySeriesData.length - 1 - index]
          : null);
      const seriesConfig: any = {
        ...s,
        xAxisIndex: 0,
        ...(barWidth ? { barMaxWidth: barWidth } : {}),
        ...(seriesColor ? { itemStyle: { color: seriesColor } } : {}),
      };

      seriesConfig.xAxisIndex = 0;

      if (showDataLabels) {
        seriesConfig.label = {
          show: true,
          position: dataLabelPosition,
          formatter: (params: any) => {
            if (stackType === 'percentage') {
              return this.formatRoundedPercent(params.value);
            }

            if (this.data?.chartConfig?.showPercentage) {
              const formattedValue = formatValueLabel(params.value);
              const total = this.currentTotals[params.dataIndex] || 0;
              const percentageNum =
                total > 0 ? Math.round((params.value / total) * 100) : 0;
              return `${formattedValue}\n(${percentageNum}%)`;
            }
            return formatValueLabel(params.value);
          },
          fontSize: 10,
          color: dataLabelPosition === 'inside' ? '#fff' : '#333',
        };
      }

      return seriesConfig;
    });

    // Build final series array
    const allSeries: any[] = [...seriesWithLabels];

    // Add line series if configured
    if (lineSeriesData.length > 0) {
      const lineColor = config.lineColor || '#FF6B6B';
      const lineSeriesWithLabels = lineSeriesData.map((s) => {
        const seriesConfig: any = {
          ...s,
          smooth: smoothLine,
          yAxisIndex: 1,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2,
            color: lineColor,
          },
          itemStyle: { color: lineColor },
        };

        if (showDataLabels) {
          seriesConfig.label = {
            show: true,
            position: 'bottom',
            formatter: (params: any) => this.formatRoundedPercent(params.value),
            fontSize: 10,
            color: '#ffffff',
          };
        }

        return seriesConfig;
      });

      // Add line series to the array (after bars, before Total)
      allSeries.push(...lineSeriesWithLabels);
    }

    // Add total series if enabled
    if (showTotalLabels) {
      allSeries.push({
        name: 'Total',
        type: 'bar',
        stack: 'total',
        data: this.currentTotals.map(() => 0),
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) =>
            stackType === 'percentage'
              ? '100%'
              : formatValueLabel(this.currentTotals[params.dataIndex]),
          fontSize: 11,
          fontWeight: 'bold',
          offset: [0, 5],
          color: '#333',
        },
        itemStyle: { color: 'transparent' },
        emphasis: { itemStyle: { color: 'transparent' } },
      });
    }

    // --- LEGEND FADE RULE: if series total === 0, fade its legend text ---
    // Build legend config (FADE legend text + symbol when series total = 0)

    // helper: series total
    const seriesTotal = (s: any): number =>
      (s?.data || []).reduce((sum: number, v: any) => {
        const num = typeof v === 'object' ? Number(v?.value ?? 0) : Number(v ?? 0);
        return sum + (isNaN(num) ? 0 : num);
      }, 0);

    // Build a map of seriesName -> actual color used in bars
    // (seriesWithLabels already has itemStyle.color applied)
    const seriesColorByName: Record<string, string | undefined> = {};
    seriesWithLabels.forEach((s: any) => {
      seriesColorByName[s.name] = s?.itemStyle?.color;
    });

    // build legend entries for bars plus optional line series
    const legendEntries: any[] = sortedSeriesData.map((s: any) => {
      const faded = seriesTotal(s) === 0;
      const baseColor = seriesColorByName[s.name]; // same color as the bar

      return {
        name: s.name,
        textStyle: { color: faded ? 'rgba(120,120,120,0.35)' : '#333' },
        itemStyle: { color: faded ? 'rgba(120,120,120,0.25)' : (baseColor || '#333') },
      };
    });

    // add line series legend entry if present
    if (lineSeriesData.length > 0) {
      const lineColor = config.lineColor || '#FF6B6B';
      lineSeriesData.forEach((ls: any) => {
        legendEntries.unshift({
          name: ls.name,
          textStyle: { color: '#333' },
          itemStyle: { color: lineColor },
        });
      });
    }

    const legendConfig: any = {
      show: showLegend,
      type: 'scroll',
      textStyle: { fontSize: 10 },
      data: legendEntries.reverse(),
    };




    if (legendPosition === 'top') legendConfig.top = 0;
    else if (legendPosition === 'bottom') legendConfig.bottom = 0;
    else if (legendPosition === 'left') {
      legendConfig.left = 0;
      legendConfig.orient = 'vertical';
    } else if (legendPosition === 'right') {
      legendConfig.right = 0;
      legendConfig.orient = 'vertical';
    }

    // Build chart option

    const yearAxisOffset = Number(config.yearAxisOffset ?? 22);

    const xAxisConfig = this.enableQuarterYearAxis
      ? ([
        {
          type: 'category',
          data: this.xAxisQuarters,
          triggerEvent: true,
          axisLabel: {
            clickable: true,
            rotate: 0
          },
          axisTick: { alignWithLabel: true },
        },
        {
          type: 'category',
          data: this.xAxisYears,
          boundaryGap: false,
          position: 'bottom',
          offset: yearAxisOffset,
          triggerEvent: true,
          axisLabel: {
            clickable: true,
            interval: 0,
            rotate: 0
          },
          axisTick: {
            show: false,
          },
          axisLine: { show: true },
        },
      ] as any)
      : ({
        type: 'category',
        data: categories,
        triggerEvent: true,
        axisLabel: {
          clickable: true,
          interval: 0,
          rotate: config.xLabelOrientation === 'horizontal' ? 0 : categories.length > 12 ? 45 : 0
        },
        axisTick: { alignWithLabel: true },
      } as any);

    const option: any = {
      tooltip: {
        trigger: 'item',
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: [10, 14],
        textStyle: { color: '#333', fontSize: 13 },
        formatter: (params: any) => {
          if (params.seriesName === 'Total') return '';
          const categoryIndex = params.dataIndex;
          const total = this.currentTotals[categoryIndex];
          const rawSeriesValue =
            typeof params.value === 'object' ? (params.value?.value ?? 0) : params.value;
          const isLineSeries = params.seriesType === 'line';
          const quarter = this.xAxisQuarters?.[categoryIndex] || '';
          const year = this.xAxisYearsRaw?.[categoryIndex] || '';
          const fallbackCategory = this.allCategories?.[categoryIndex] || params.name || '';
          const configuredTimeView = String(
            this.data?.chartConfig?.tooltipTimeView || '',
          ).toLowerCase();
          const isQuarterlyTooltip =
            configuredTimeView === 'quarterly' ||
            (configuredTimeView !== 'yearly' && this.enableQuarterYearAxis);
          const timeLabel = year || fallbackCategory;
          const qtrLabel = [year, quarter].filter(Boolean).join(' ');
          const timeBlock = isQuarterlyTooltip
            ? `<div><b>Year Quarter:</b> ${qtrLabel || '-'}</div>`
            : `<div><b>Year:</b> ${timeLabel}</div>`;

          // Derive human-readable series label from seriesKey
          // e.g. "segment" → "Segment", "engine_model" → "Engine Model"
          const rawSeriesKey: string = (this.data?.seriesKey || '') as string;
          let seriesLabel: string =
            rawSeriesKey
              .split('_')
              .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ') || 'Segment';

          // Identify which overview variant is active
          const isMarginWidget = this.data?.widgetId === '$ Sales trend by Region' || this.data?.widgetId === '$ Sales trend by Segment';
          const isAdvancedView = configuredTimeView === 'yearly' || configuredTimeView === 'quarterly' || isMarginWidget;
          const isAdvancedPct =
            isAdvancedView &&
            this.data?.chartConfig?.showPercentage === true;

          if (this.data?.widgetId === '$ Sales trend by Region') {
            seriesLabel = 'Region';
          } else if (this.data?.widgetId === '$ Sales trend by Segment') {
            seriesLabel = 'Segment';
          }

          if (this.data?.widgetId?.includes('MSBI out Gross Sales trend by Region')) {
            seriesLabel = 'Region';
          }

          // Contribution % relative to the year total
          const pct =
            total > 0 ? ((rawSeriesValue / total) * 100).toFixed(2) : '0.00';

          // ── stackType=percentage charts (100% stacked): legacy format ────────
          if (stackType === 'percentage') {
            const originalSeries = this.chartSeriesData.find(
              (s) => s.name === params.seriesName,
            );
            const originalValue = originalSeries?.data[categoryIndex] || 0;
            return `
              <div style="line-height: 1.8;">
                ${timeBlock}
                <div><b>${seriesLabel}:</b> ${params.seriesName}</div>
                <div><b>Percentage:</b> ${this.formatRoundedPercent(params.value)}</div>
                <div><b>Value:</b> ${formatNumberWithCommas(originalValue)}</div>
                <div><b>Total:</b> ${formatNumberWithCommas(total)}</div>
              </div>
            `;
          }

          // Prepare MSBI Gross Sales Line if applicable for MSBI charts
          const isMsbiEngineFamily = this.data?.widgetId === 'MSBI out Gross Sales trend by Engine Family';
          let extraMsbiLine = '';
          if (isMsbiEngineFamily && !isLineSeries) {
            const seriesNameStr = String(params.seriesName || '').trim().toLowerCase();
            const originalSeriesForMsbi = this.chartSeriesData?.find((s) =>
              String(s.name || '').trim().toLowerCase() === seriesNameStr ||
              String(s.originalName || '').trim().toLowerCase() === seriesNameStr
            );

            // params.dataIndex strictly maps to the array index
            const rawMsbiDataPoint = originalSeriesForMsbi?.data?.[params.dataIndex];

            let val = rawMsbiDataPoint?.grossMarginPercent ?? rawMsbiDataPoint?.grossMargin;

            if (val === undefined) {
              val = params.data?.grossMarginPercent ?? params.data?.grossMargin;
            }

            if (val === undefined && this.chartLineSeriesData?.length > 0) {
              val = this.chartLineSeriesData[0].data?.[params.dataIndex]?.value;
            }

            if (val !== undefined && val !== null && val !== '') {
              const numVal = Number(val);
              const formattedVal = !isNaN(numVal) ? numVal.toFixed(2) + '%' : (val ? val + '%' : '0%');
              extraMsbiLine = `<div><b>Gross Margin %:</b> ${formattedVal}</div>`;
            } else {
              // Try the data points directly from params.data
              const dataPoint = params.data;
              const directVal = dataPoint?.grossMarginPercent ?? dataPoint?.grossMargin ?? dataPoint?.grossSalesPercent;
              if (directVal !== undefined && directVal !== null && directVal !== '') {
                const numDirectVal = Number(directVal);
                const formattedDirectVal = !isNaN(numDirectVal) ? numDirectVal.toFixed(2) + '%' : (directVal ? directVal + '%' : '0%');
                extraMsbiLine = `<div><b>Gross Margin %:</b> ${formattedDirectVal}</div>`;
              }
            }
          }

          // ── Yearly/Quarterly Overview ($+%): no %GT Total Sales ──────────────────
          if (isAdvancedPct) {
            return `
              <div style="line-height: 1.8;">
                ${timeBlock}
                <div><b>${seriesLabel}:</b> ${params.seriesName}</div>
                <div><b>Total Sales:</b> ${formatNumberWithCommas(rawSeriesValue)} (${pct}%)</div>
                <div><b>Total:</b> ${formatNumberWithCommas(total)}</div>
              </div>
            `;
          }

          // ── Yearly/Quarterly Overview ($): no %GT Total Sales ──────────────────────────
          if (isAdvancedView) {
            return `
              <div style="line-height: 1.8;">
                ${timeBlock}
                <div><b>${seriesLabel}:</b> ${params.seriesName}</div>
                ${extraMsbiLine}
                <div><b>Total Sales:</b> ${isLineSeries ? this.formatRoundedPercent(rawSeriesValue) : `${formatNumberWithCommas(rawSeriesValue)} (${pct}%)`}</div>
                <div><b>Total:</b> ${formatNumberWithCommas(total)}</div>
              </div>
            `;
          }

          // ── All other charts: unchanged legacy format ────────────────────────
          return `
            <div style="line-height: 1.8;">
              ${timeBlock}
              <div><b>${seriesLabel}:</b> ${params.seriesName}</div>
              <div><b>Total Sales:</b> ${isLineSeries ? this.formatRoundedPercent(rawSeriesValue) : formatNumberWithCommas(rawSeriesValue)}</div>
              <div><b>Total:</b> ${formatNumberWithCommas(total)}</div>
            </div>
          `;
        },
      },
      legend: legendConfig,
      grid: {
        left: '3%',
        right: lineSeriesData.length > 0 ? '8%' : '4%', // Add more right margin for secondary Y-axis
        top: legendPosition === 'top' && showLegend ? '10%' : '5%',
        // Use the ORIGINAL enableQuarterYearAxis for grid bottom, not the current drill state.
        // During drill up/down, enableQuarterYearAxis is temporarily set to false, which would
        // change the bottom value and shift the x-axis position. By pinning to the original
        // config value (_origEnableQuarterYear when drilling, else the current value), the
        // plot area stays anchored and the x-axis doesn't jump during drill transitions.
        bottom: (() => {
          const baseIsQuarterly = this._origEnableQuarterYear !== null
            ? this._origEnableQuarterYear
            : this.enableQuarterYearAxis;
          return baseIsQuarterly ? (Number(yearAxisOffset) + 15) : 20;
        })(),
        containLabel: true,
      },

      xAxis: xAxisConfig,
      yAxis: lineSeriesData.length > 0
        ? [
          {
            type: 'value',
            // hide left axis completely (no labels or name)
            name: '',
            position: 'left',
            splitLine: {
              show: false,
            },
            axisLabel: {
              show: false,
            },
            min: 0,
            max: (value: any) => {
              return value.max > 0 ? value.max * 1.15 : 100;
            }
          },
          {
            type: 'value',
            name: '',
            position: 'right',
            splitLine: {
              show: false,
            },
            axisLabel: {
              show: true,
              formatter: (value: number) => this.formatRoundedPercent(value),
            },
            min: config.rightAxisMin ?? 40,
            max: 100,
          },
        ]
        : {
          type: 'value',
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: showYAxisLabels,
            formatter:
              stackType === 'percentage'
                ? (value: number) => this.formatRoundedPercent(value)
                : undefined,
          },
          ...(stackType === 'percentage' ? { max: 100 } : {
            min: 0,
            max: (value: any) => value.max > 0 ? value.max * 1.15 : 100
          }),
        },

      series: allSeries,
    };

    if (this.myChart) {
      this.myChart.setOption(option, true);
      this.syncLegendSelectionFromFilters();
      if (this.currentlySelectedIndices.length > 0) {
        this.selectLegendsByIndices(this.currentlySelectedIndices);
      }
      this.myChart.hideLoading();
      this.setupChartClickEvent();
      this.setupLegendClickEvent();
    }
  }

  private showNoDataState(): void {
    if (!this.myChart) return;
    this.myChart.hideLoading();
    this.myChart.setOption(
      {
        xAxis: { show: false },
        yAxis: { show: false },
        series: [],
        legend: { show: false },
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: this.noDataText,
              fill: '#666',
              fontSize: 14,
              fontFamily: 'sans-serif',
            },
          },
        ],
      },
      true,
    );
  }

  protected override broadcastLegendSync(selectedIndices: number[]): void {
    const listenerConfig =
      this.data?.legendListener ||
      this.data?.legendListner ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const config = this.data?.chartConfig || {};
    const positionColors = config.topNColorsByStackPosition || [];
    const legendKey =
      this.data?.legendKey ||
      this.data?.legendFilterKey ||
      this.data?.legendFilterkey ||
      this.data?.filterKey;

    const selectedColors = selectedIndices
      .map((idx) => positionColors[this.allSeriesNames.length - 1 - idx])
      .filter(Boolean);
    const colorSyncEnabled = config.colorSyncEnabled === true;

    const filterData = buildLegendSyncData(
      selectedIndices,
      this.originalSeriesNames, // USE ORIGINAL NAMES FOR SYNC
      this.data.widgetId,
      listenerConfig,
      { colorSyncEnabled, selectedColors, legendKey },
    );

    this.emitLegendSyncWithCurrentFilters(filterData);
  }

  protected override broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig = this.data?.filterListener || this.data?.filterListner;
    const filterData = buildCategoryFilterData(categories, this.data.widgetId, listenerConfig);
    this.emitLegendSyncWithCurrentFilters(filterData);
  }

  private emitLegendSyncWithCurrentFilters(filterData: any): void {
    const currentFilters = this.globalFilterService.getCurrentFilters?.() || {};
    const queryParams = currentFilters?.queryParams || {};
    const requestParams = currentFilters?.requestParams || {};

    (this.globalFilterService as any).filterSubject.next({
      queryParams,
      requestParams,
      ...filterData,
    });
  }

  // protected override setupChartClickEvent(): void {
  //   this.myChart.off('click');
  //   this.myChart.on('click', (params: any) => {
  //     // Check if clicked on x-axis label and sync enabled
  //     if (
  //       params.componentType === 'xAxis' &&
  //       this.data?.chartConfig?.syncXaxisLabels
  //     ) {
  //       this.handleXAxisLabelClick(params);
  //     } else {
  //       this.updateFilterSelection(params);
  //     }
  //   });
  // }

  /**
   * Clear slice-based filters for this chart.
   * This explicitly removes the series/year/qtr keys from the global filter payload
   * instead of sending empty arrays, so the backend receives a true "no filters" state.
   */
  private clearSliceFilter(): void {
    const enableQtr = !!this.data?.chartConfig?.enableQuarterYearAxis;
    // Only clear series key by default; keep year/qtr if x-axis category selection is active.
    const shouldClearTimeKeys = !(this.selectedCategories && this.selectedCategories.length > 0);
    this.clearSliceFiltersForKeys(enableQtr && shouldClearTimeKeys, false, shouldClearTimeKeys);
    const seriesKey = this.data?.seriesKey || 'segment';

    const existingKeys: string[] = Array.isArray(this.data?.filterKey)
      ? [...this.data.filterKey]
      : String(this.data?.filterKey || '')
        .split(',')
        .map((k: string) => k.trim())
        .filter(Boolean);

    const keysToClear = Array.from(
      new Set([
        ...existingKeys,
        seriesKey,
        ...(shouldClearTimeKeys ? ['year', ...(enableQtr ? ['qtr'] : [])] : []),
      ]),
    );
    const clonedConfig = { ...this.data, filterKey: keysToClear };

    const clearPayload: any = { [seriesKey]: [] };
    if (shouldClearTimeKeys) {
      clearPayload.year = [];
      if (enableQtr) clearPayload.qtr = [];
    }
    this.chartFilterHandler.updateFilterSelection(clonedConfig, clearPayload);
  }

  protected override setupChartClickEvent(): void {
    this.myChart.off('click');

    this.myChart.on('click', (params: any) => {
      // Legend clicks are handled in `legendselectchanged` only.
      if (params?.componentType === 'legend') {
        const nativeEvent = params?.event?.event;
        nativeEvent?.preventDefault?.();
        nativeEvent?.stopPropagation?.();
        return;
      }

      // Ignore chart background / non-data clicks.
      if (
        params?.componentType !== 'series' &&
        params?.componentType !== 'xAxis'
      ) {
        return;
      }

      // Ignore non-datapoint series events (can occur during legend interactions).
      if (
        params?.componentType === 'series' &&
        !Number.isInteger(params?.dataIndex)
      ) {
        return;
      }

      // keep x-axis label behavior unchanged
      if (
        params.componentType === 'xAxis' &&
        (this.data?.chartConfig?.syncXaxisLabels ?? true)
      ) {
        this.handleXAxisLabelClick(params);
        return;
      }

      // stacked bar slice click (one colored area in a bar)
      if (
        params.componentType === 'series' &&
        params.seriesType === 'bar' &&
        params.seriesName &&
        Number.isInteger(params.dataIndex) &&
        params.seriesName !== 'Total'
      ) {
        const nativeEvent = params.event?.event || params.event;
        const ctrlOrCmd = !!(nativeEvent?.ctrlKey || nativeEvent?.metaKey);
        const clickedCategory = this.allCategories?.[params.dataIndex];

        const clicked = { seriesName: params.seriesName, dataIndex: params.dataIndex };
        const clickedKey = this.sliceKey(clicked);

        const existingIndex = this.selectedSlices.findIndex(
          s => this.sliceKey(s) === clickedKey
        );

        if (!ctrlOrCmd) {
          // single select toggle
          if (existingIndex !== -1 && this.selectedSlices.length === 1) {
            this.selectedSlices = [];
            this.selectedCategories = [];
          } else {
            this.selectedSlices = [clicked];
            this.selectedCategories = clickedCategory ? [clickedCategory] : [];
          }
        } else {
          // multi-select toggle
          if (existingIndex !== -1) {
            this.selectedSlices = this.selectedSlices.filter((_, i) => i !== existingIndex);
          } else {
            this.selectedSlices = [...this.selectedSlices, clicked];
          }

          const selectedCategories = new Set(this.selectedCategories || []);
          if (clickedCategory) {
            if (existingIndex === -1) {
              selectedCategories.add(clickedCategory);
            } else {
              const hasRemainingSliceForCategory = this.selectedSlices.some(
                (slice) => this.getCategoryForSlice(slice) === clickedCategory,
              );
              if (!hasRemainingSliceForCategory) {
                selectedCategories.delete(clickedCategory);
              }
            }
          }
          this.selectedCategories = this.normalizeCategorySelection(
            Array.from(selectedCategories),
          );
        }

        this.reconcileSlicesWithSelectedCategories(false);
        this.refreshSelectionHighlights();
        this.emitCombinedSelectionFilters();

        return;
      }

      // fallback (keep your existing behavior)
      this.updateFilterSelection(params);
    });
  }







  private broadcastSliceFilter(): void {
    this.emitCombinedSelectionFilters();
  }

  private buildSliceFilterUpdate(
    slices: Array<{ seriesName: string; dataIndex: number }>,
  ): { config: any; payload: any } | null {
    if (!slices?.length) return null;

    const toOriginal = (uiName: string): string => {
      const idx = this.allSeriesNames.indexOf(uiName);
      return idx !== -1 ? (this.originalSeriesNames[idx] || uiName) : uiName;
    };

    const seriesKey = this.data?.seriesKey || 'segment';

    // Keep duplicates so series/year/qtr remain index-aligned for zipped pairing.
    const seriesValues: string[] = slices
      .map((s) => toOriginal(s.seriesName))
      .filter(Boolean);

    const yearValues: string[] = slices
      .map((s) => this.extractYearFromCategoryLocal(this.allCategories?.[s.dataIndex] || ''))
      .filter(Boolean);

    const enableQtr = !!this.data?.chartConfig?.enableQuarterYearAxis;

    // In drilled-down quarterly state, always extract quarter values even if enableQtr is off
    const shouldExtractQtr = enableQtr || this.drillState === 'quarterly';
    const effectiveYears = yearValues.length > 0 ? yearValues : (this.drillState === 'quarterly' ? this.drilledYears : []);

    const qtrValues: string[] = shouldExtractQtr
      ? slices
        .map((s) => this.extractQuarterFromCategoryLocal(this.allCategories?.[s.dataIndex] || ''))
        .filter(Boolean)
      : [];

    // If there is no series selection and we only have year/quarter information,
    // treat this as a "clear filters" action (payload must be effectively empty).
    const hasSeries = seriesValues.length > 0;
    const hasYearOrQtr = yearValues.length > 0 || qtrValues.length > 0;
    if (!hasSeries && hasYearOrQtr) return null;

    const existingKeys: string[] = Array.isArray(this.data?.filterKey)
      ? [...this.data.filterKey]
      : String(this.data?.filterKey || '')
        .split(',')
        .map((k: string) => k.trim())
        .filter(Boolean);

    const config = {
      ...this.data,
      filterKey: Array.from(
        new Set([
          ...existingKeys,
          seriesKey,
          ...(effectiveYears.length > 0 ? ['year'] : []),
          ...(shouldExtractQtr && qtrValues.length > 0 ? ['qtr'] : []),
        ]),
      ),
    };

    const payload: any = {
      [seriesKey]: seriesValues.length === 1 ? seriesValues[0] : seriesValues,
      ...(effectiveYears.length > 0
        ? { year: effectiveYears.length === 1 ? effectiveYears[0] : effectiveYears }
        : {}),
      ...(shouldExtractQtr && qtrValues.length > 0
        ? { qtr: qtrValues.length === 1 ? qtrValues[0] : qtrValues }
        : {}),
    };

    return { config, payload };
  }

  private getSelectedLegendUiSeriesValues(): string[] {
    return this.currentlySelectedIndices
      .map((i: number) => this.allSeriesNames[i])
      .filter(Boolean);
  }

  private getSelectedLegendOriginalSeriesValues(): string[] {
    return this.currentlySelectedIndices
      .map((i: number) => this.originalSeriesNames[i] || this.allSeriesNames[i])
      .filter(Boolean);
  }

  private getRelevantSlicesForCurrentState(): Array<{ seriesName: string; dataIndex: number }> {
    const selectedLegendNames = this.getSelectedLegendUiSeriesValues();
    const hasLegendFilter = selectedLegendNames.length > 0;
    const selectedCategorySet = new Set(this.selectedCategories || []);
    const hasCategoryFilter = selectedCategorySet.size > 0;

    return (this.selectedSlices || []).filter((slice) => {
      const matchesLegend = !hasLegendFilter || selectedLegendNames.includes(slice.seriesName);
      const category = this.allCategories?.[slice.dataIndex] || '';
      const matchesCategory = !hasCategoryFilter || selectedCategorySet.has(category);
      return matchesLegend && matchesCategory;
    });
  }

  private buildCategoryFilterUpdate(
    categories: string[],
    legendSeriesValues: string[],
  ): { config: any; payload: any } | null {
    const enableQtr = !!this.data?.chartConfig?.enableQuarterYearAxis;
    const seriesKey = this.data?.seriesKey || 'segment';

    const yearValues = categories
      .map((cat: string) => this.extractYearFromCategoryLocal(cat))
      .filter(Boolean);

    const effectiveYears = yearValues.length > 0 ? yearValues : (this.drillState === 'quarterly' ? this.drilledYears : []);

    // When drilled down to quarterly view, categories are just 'Q1', 'Q2', etc.
    // with no year info — allow quarter-only filter in that case.
    if (!yearValues.length && this.drillState !== 'quarterly') return null;

    if (!enableQtr) {
      const payload: any = {
        year: effectiveYears.length === 1 ? effectiveYears[0] : effectiveYears,
      };
      const config: any = { ...this.data, filterKey: ['year'] };
      if (legendSeriesValues.length > 0) {
        payload[seriesKey] =
          legendSeriesValues.length === 1 ? legendSeriesValues[0] : legendSeriesValues;
        config.filterKey = [seriesKey, 'year'];
      }
      return { config, payload };
    }

    const qtrValues = categories
      .map((cat: string) => this.extractQuarterFromCategoryLocal(cat))
      .filter(Boolean);

    if (!qtrValues.length && this.drillState !== 'quarterly') return null;

    const payload: any = {} as any;
    if (effectiveYears.length) {
      payload.year = effectiveYears.length === 1 ? effectiveYears[0] : effectiveYears;
    }
    if (qtrValues.length) {
      payload.qtr = qtrValues.length === 1 ? qtrValues[0] : qtrValues;
    }

    const filterKeys = ['year', 'qtr'].filter(k => k === 'qtr' ? qtrValues.length > 0 : effectiveYears.length > 0);
    const config: any = { ...this.data, filterKey: filterKeys };
    if (legendSeriesValues.length > 0) {
      payload[seriesKey] =
        legendSeriesValues.length === 1 ? legendSeriesValues[0] : legendSeriesValues;
      config.filterKey = [seriesKey, ...filterKeys];
    }

    return { config, payload };
  }

  private emitCombinedSelectionFilters(): void {
    const enableQtr = !!this.data?.chartConfig?.enableQuarterYearAxis;
    const legendOriginalValues = this.getSelectedLegendOriginalSeriesValues();
    const relevantSlices = this.getRelevantSlicesForCurrentState();

    if (relevantSlices.length > 0) {
      const sliceFilterUpdate = this.buildSliceFilterUpdate(relevantSlices);
      if (sliceFilterUpdate) {
        this.clearSliceFiltersForKeys(enableQtr, true, true);
        this.chartFilterHandler.updateFilterSelection(
          sliceFilterUpdate.config,
          sliceFilterUpdate.payload,
        );
        return;
      }
    }

    if (this.selectedCategories?.length) {
      const categoryFilterUpdate = this.buildCategoryFilterUpdate(
        this.selectedCategories,
        legendOriginalValues,
      );
      if (categoryFilterUpdate) {
        this.clearSliceFiltersForKeys(enableQtr, true, true);
        this.chartFilterHandler.updateFilterSelection(
          categoryFilterUpdate.config,
          categoryFilterUpdate.payload,
        );
        return;
      }
    }

    if (legendOriginalValues.length > 0) {
      this.clearSliceFiltersForKeys(enableQtr, true, true);
      const legendPayload =
        legendOriginalValues.length === 1
          ? legendOriginalValues[0]
          : legendOriginalValues;
      this.chartFilterHandler.updateFilterSelection(
        this.getLegendClonedConfig(),
        legendPayload,
      );
      return;
    }

    this.clearSliceFiltersForKeys(enableQtr, false, true);
  }

  private refreshSelectionHighlights(): void {
    const relevantSlices = this.getRelevantSlicesForCurrentState();
    if (relevantSlices.length > 0) {
      this.highlightSlices(relevantSlices);
      return;
    }

    if (this.selectedCategories?.length) {
      this.highlightCategories(this.selectedCategories);
      return;
    }

    this.resetSliceHighlight();
  }

  private applyLegendAwareSliceFilter(_fallbackLegendValue: any): void {
    this.refreshSelectionHighlights();
    this.emitCombinedSelectionFilters();
  }

  /**
   * Helper used by both clearSliceFilter and broadcastSliceFilter to
   * remove slice-related keys (series/year/qtr) from the global filters.
   */
  private clearSliceFiltersForKeys(
    includeQtr: boolean,
    suppressEmit: boolean = false,
    includeYear: boolean = true,
  ): void {
    const seriesKey = this.data?.seriesKey || 'segment';
    const listener =
      this.data?.legendListener ||
      this.data?.legendListner ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const existingKeys: string[] = Array.isArray(this.data?.filterKey)
      ? [...this.data.filterKey]
      : String(this.data?.filterKey || '')
        .split(',')
        .map((k: string) => k.trim())
        .filter(Boolean);

    const keysToClear = Array.from(
      new Set([
        ...existingKeys,
        seriesKey,
        ...(includeYear ? ['year'] : []),
        ...(includeQtr ? ['qtr'] : []),
      ]),
    );

    if (suppressEmit) {
      keysToClear.forEach((key) => this.globalFilterService.deleteKey(key, false));
      return;
    }

    keysToClear.forEach((key, index) => {
      const isLast = index === keysToClear.length - 1;
      if (isLast) {
        this.globalFilterService.resetFilterSelection(key, false, listener);
      } else {
        this.globalFilterService.deleteKey(key, false);
      }
    });
  }

  protected override handleXAxisLabelClick(params: any): void {
    if (!this.data?.chartConfig?.enableQuarterYearAxis) {
      const clickedCategory = params.value;
      this.applyCategorySelectionWithoutLegendReset(clickedCategory, params);
      return;
    }

    const axisIndex = params?.axisIndex;
    const idx = params?.valueIndex ?? params?.dataIndex;
    if (idx == null || idx < 0) return;

    const rawValue = String(params?.value ?? '').trim();
    const isYearValue = /^(19|20)\d{2}$/.test(rawValue);
    const isQuarterValue = /^Q\s*[1-4]$/i.test(rawValue);

    if (axisIndex === 0 || (axisIndex == null && isQuarterValue)) {
      const fullCategory = this.allCategories[idx];
      this.applyCategorySelectionWithoutLegendReset(fullCategory, params);
      return;
    }

    if (axisIndex === 1 || (axisIndex == null && isYearValue)) {
      const clickedYear =
        this.yearAxisIndexToYear[idx] ||
        this.xAxisYearsRaw[idx] ||
        this.xAxisYears[idx] ||
        rawValue;
      if (!clickedYear) return;

      const nativeEvent = params.event?.event || params.event;
      const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;

      const selectedYears = this.getSelectedYearsFromCategories();
      const isYearFullySelected = this.isYearFullySelected(clickedYear);

      const nextYears = ctrlPressed
        ? selectedYears.includes(clickedYear)
          ? selectedYears.filter((y) => y !== clickedYear)
          : [...selectedYears, clickedYear]
        : selectedYears.length === 1 &&
          selectedYears[0] === clickedYear &&
          isYearFullySelected
          ? []
          : [clickedYear];

      const nextCategories =
        nextYears.length === 0
          ? []
          : this.allCategories.filter((c: string) =>
            nextYears.includes(this.extractYearFromCategoryLocal(c)),
          );

      if (nextCategories.length === 0) {
        this.selectedCategories = [];
        this.reconcileSlicesWithSelectedCategories(true);
        this.refreshSelectionHighlights();
        this.updateXAxisFiltersFromSelectedCategories();
        this.broadcastCategoryFilter([]);
      } else {
        this.selectedCategories = this.normalizeCategorySelection(nextCategories);
        this.reconcileSlicesWithSelectedCategories(false);
        this.refreshSelectionHighlights();
        this.updateXAxisFiltersFromSelectedCategories();
        this.broadcastCategoryFilter(this.selectedCategories);
      }
      return;
    }

    if (isYearValue) {
      const clickedYear = rawValue;
      const nativeEvent = params.event?.event || params.event;
      const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;

      const selectedYears = this.getSelectedYearsFromCategories();
      const isYearFullySelected = this.isYearFullySelected(clickedYear);

      const nextYears = ctrlPressed
        ? selectedYears.includes(clickedYear)
          ? selectedYears.filter((y) => y !== clickedYear)
          : [...selectedYears, clickedYear]
        : selectedYears.length === 1 &&
          selectedYears[0] === clickedYear &&
          isYearFullySelected
          ? []
          : [clickedYear];

      const nextCategories =
        nextYears.length === 0
          ? []
          : this.allCategories.filter((c: string) =>
            nextYears.includes(this.extractYearFromCategoryLocal(c)),
          );

      if (nextCategories.length === 0) {
        this.selectedCategories = [];
        this.reconcileSlicesWithSelectedCategories(true);
        this.refreshSelectionHighlights();
        this.updateXAxisFiltersFromSelectedCategories();
        this.broadcastCategoryFilter([]);
      } else {
        this.selectedCategories = this.normalizeCategorySelection(nextCategories);
        this.reconcileSlicesWithSelectedCategories(false);
        this.refreshSelectionHighlights();
        this.updateXAxisFiltersFromSelectedCategories();
        this.broadcastCategoryFilter(this.selectedCategories);
      }
    }
  }

  private applyCategorySelectionWithoutLegendReset(clickedCategory: any, params: any): void {
    if (!clickedCategory) return;

    const nativeEvent = params.event?.event || params.event;
    const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;

    if (ctrlPressed) {
      const pos = this.selectedCategories.indexOf(clickedCategory);
      this.selectedCategories =
        pos === -1
          ? [...this.selectedCategories, clickedCategory]
          : this.selectedCategories.filter((c) => c !== clickedCategory);
    } else {
      this.selectedCategories =
        this.selectedCategories.length === 1 && this.selectedCategories[0] === clickedCategory
          ? []
          : [clickedCategory];
    }

    if (this.selectedCategories.length === 0) {
      this.reconcileSlicesWithSelectedCategories(true);
      this.refreshSelectionHighlights();
      this.updateXAxisFiltersFromSelectedCategories();
      this.broadcastCategoryFilter([]);
    } else {
      this.selectedCategories = this.normalizeCategorySelection(
        this.selectedCategories,
      );
      this.reconcileSlicesWithSelectedCategories(false);
      this.refreshSelectionHighlights();
      this.updateXAxisFiltersFromSelectedCategories();
      this.broadcastCategoryFilter(this.selectedCategories);
    }
  }

  private extractYearFromCategoryLocal(cat: string): string {
    const m = String(cat).match(/(19|20)\d{2}/);
    return m ? m[0] : '';
  }

  private extractQuarterFromCategoryLocal(cat: string): string {
    const str = String(cat ?? '').toUpperCase();
    const qMatch = str.match(/\bQ\s*([1-4])\b/);
    if (qMatch) return `Q${qMatch[1]}`;

    const quarterWordMatch = str.match(/\bQUARTER\s*([1-4])\b/);
    if (quarterWordMatch) return `Q${quarterWordMatch[1]}`;

    const qtrMatch = str.match(/\bQTR\s*([1-4])\b/);
    if (qtrMatch) return `Q${qtrMatch[1]}`;

    return '';
  }

  protected override updateXAxisFiltersFromSelectedCategories(): void {
    this.emitCombinedSelectionFilters();
  }








  private highlightSlices(slices: Array<{ seriesName: string; dataIndex: number }>): void {
    if (!this.myChart) return;

    const option = this.myChart.getOption() as any;
    if (!option?.series) return;
    const selectedSet = new Set(slices.map(s => this.sliceKey(s)));

    const updatedSeries = option.series.map((s: any) => {
      if (s?.name === 'Total' || !Array.isArray(s.data)) return s;

      const seriesName = s.name;

      const newData = s.data.map((dp: any, i: number) => {
        const rawValue = typeof dp === 'object' ? (dp?.value ?? dp) : dp;
        const isSelected = selectedSet.has(this.sliceKey({ seriesName, dataIndex: i }));

        return {
          ...(typeof dp === 'object' ? dp : {}),
          value: rawValue,
          itemStyle: { ...(dp?.itemStyle || {}), opacity: isSelected ? 1 : 0.4 },
          label: { ...(dp?.label || {}), opacity: isSelected ? 1 : 0 },
        };
      });

      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  private resetSliceHighlight(): void {
    if (!this.myChart) return;

    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (s?.name === 'Total' || !Array.isArray(s.data)) return s;

      const newData = s.data.map((dp: any) => {
        const rawValue = typeof dp === 'object' ? (dp?.value ?? dp) : dp;
        return {
          ...(typeof dp === 'object' ? dp : {}),
          value: rawValue,
          itemStyle: { ...(dp?.itemStyle || {}), opacity: 1 },
          label: { ...(dp?.label || {}), opacity: 1 },
        };
      });

      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }



  protected override updateFilterSelection(params: any): void {
    if (!this.data?.filterKey) return;

    const filterKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : this.data.filterKey.split(',').map((k: string) => k.trim());

    const payload: any = {};

    filterKeys.forEach((key: string) => {
      // 1. Try to get value directly from data object if it exists
      if (params.data && typeof params.data === 'object' && key in params.data) {
        const value = params.data[key];
        // If it's an object (common with complex datasets), extract the actual value for that key
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          payload[key] = value[key] ?? Object.values(value)[0];
        } else {
          payload[key] = value;
        }
        return;
      }

      // 2. Intelligent mapping based on config
      if (key === this.data.seriesKey || key === 'series' || key === 'segment') {
        const idx = this.allSeriesNames.indexOf(params.seriesName);
        payload[key] = idx !== -1 ? this.originalSeriesNames[idx] : params.seriesName;
        return;
      }

      if (
        key === this.data.xAxis ||
        (this.data.categoryFields && this.data.categoryFields.includes(key)) ||
        key === 'year' || key === 'quarter' || key === 'qtr' || key === 'category'
      ) {
        payload[key] = params.name;
        return;
      }
    });

    if (Object.keys(payload).length === 0) return;
    this.chartFilterHandler.updateFilterSelection(this.data, payload);
  }

  protected override updateLegendFilterSelection(params: any): void {
    if (this.isInternalLegendChange) return;

    const nativeEvent = params?.event?.event;
    nativeEvent?.preventDefault?.();
    nativeEvent?.stopPropagation?.();

    const clickedLegend = params.name;
    if (clickedLegend === 'Total') return;

    const clickedIndex = this.allSeriesNames.indexOf(clickedLegend);
    if (clickedIndex === -1) return;

    const ctrlPressed = !!(
      this.isCtrlPressed ||
      nativeEvent?.ctrlKey ||
      nativeEvent?.metaKey
    );

    if (ctrlPressed) {
      this.isInternalLegendChange = true;
      const pos = this.currentlySelectedIndices.indexOf(clickedIndex);
      if (pos === -1) this.currentlySelectedIndices = [...this.currentlySelectedIndices, clickedIndex];
      else this.currentlySelectedIndices = this.currentlySelectedIndices.filter(i => i !== clickedIndex);

      // Identify the line series name to exclude from broadcast
      const lineSeriesName = this.data?.lineSeriesName || 'Trend';
      const useLineSeries = this.data?.useSingleLineSeries === true;

      if (this.currentlySelectedIndices.length === 0) {
        this.selectLegendsByIndices([]);
        this.broadcastLegendSync([]);
        this.applyLegendAwareSliceFilter([]);
      } else {
        this.selectLegendsByIndices(this.currentlySelectedIndices);

        // Filter out line series metric indices before broadcasting sync
        const dimensionIndices = useLineSeries
          ? this.currentlySelectedIndices.filter(idx => this.allSeriesNames[idx] !== lineSeriesName)
          : this.currentlySelectedIndices;

        if (dimensionIndices.length > 0) {
          this.broadcastLegendSync(dimensionIndices);
          const names = dimensionIndices.map(i => this.originalSeriesNames[i]);
          this.applyLegendAwareSliceFilter(names.length === 1 ? names[0] : names);
        } else {
          // If ONLY line series remains selected, we essentially broadcast an empty dimension filter
          this.broadcastLegendSync([]);
          this.applyLegendAwareSliceFilter([]);
        }
      }
      this.isInternalLegendChange = false;
      return;
    }

    this.isInternalLegendChange = true;

    // Check if the clicked legend is the line series (which represents a metric, not a dimension)
    const lineSeriesName = this.data?.lineSeriesName || 'Trend';
    const isLineSeries = this.data?.useSingleLineSeries === true && clickedLegend === lineSeriesName;

    if (this.currentlySelectedIndices.length === 1 && this.currentlySelectedIndices[0] === clickedIndex) {
      this.currentlySelectedIndices = [];
      this.selectLegendsByIndices([]);

      // ONLY broadcast sync if it's NOT the line series metric
      if (!isLineSeries) {
        this.broadcastLegendSync([]);
        this.applyLegendAwareSliceFilter([]);
      }
    } else {
      this.currentlySelectedIndices = [clickedIndex];
      this.selectLegendsByIndices([clickedIndex]);

      // ONLY broadcast sync if it's NOT the line series metric
      if (!isLineSeries) {
        this.broadcastLegendSync([clickedIndex]);
        this.applyLegendAwareSliceFilter(this.originalSeriesNames[clickedIndex]);
      }
    }
    this.isInternalLegendChange = false;
    this.updateTotalLabels();
  }

  protected updateTotalLabels(): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    const legendState = option.legend[0].selected || {};

    // Recalculate totals based on visible series only
    this.currentTotals = this.allCategories.map((_, catIndex) => {
      return this.chartSeriesData.reduce((sum, s) => {
        // If series is explicitly hidden in legend, don't include it in total
        if (legendState[s.name] === false) return sum;

        const dataPoint = s.data[catIndex];
        const val = typeof dataPoint === 'object' ? (dataPoint?.value ?? 0) : dataPoint;
        return sum + (Number(val) || 0);
      }, 0);
    });

    // Refresh the Total series data to trigger a label re-render
    this.myChart.setOption({
      series: [
        {
          name: 'Total',
          data: this.currentTotals.map(() => 0),
        },
      ],
    });
  }

  protected override selectLegendsByIndices(selectedIndices: number[]): void {
    super.selectLegendsByIndices(selectedIndices);
    this.updateTotalLabels();
  }

  private normalizeListenerIds(
    listener: string | string[] | undefined | null,
  ): string[] {
    if (!listener) {
      return [];
    }

    const ids = Array.isArray(listener) ? listener : String(listener).split(',');
    return ids.map((id) => String(id || '').trim()).filter(Boolean);
  }

  private removeCurrentWidgetFromListener(
    listener: string | string[] | undefined | null,
  ): string | string[] | undefined {
    const normalized = this.normalizeListenerIds(listener).filter(
      (id) => id !== this.data?.widgetId,
    );

    if (normalized.length === 0) {
      return undefined;
    }

    return Array.isArray(listener) ? normalized : normalized.join(',');
  }

  private getLegendClonedConfig(): any {
    const listener =
      this.data?.legendListener ||
      this.data?.legendListner ||
      this.data?.filterListener ||
      this.data?.filterListner;
    const sanitizedListener = this.removeCurrentWidgetFromListener(listener);
    const key = this.data?.legendFilterKey || this.data?.legendFilterkey;

    // Override: Use 'region' instead of 'sub_region' for the 'INTERNATIONAL'/'NORTH AMERICA' aggregate legend
    const currentLegendValues = this.getSelectedLegendOriginalSeriesValues();
    const isAggregate =
      currentLegendValues.includes('INTERNATIONAL') ||
      currentLegendValues.includes('NORTH AMERICA');

    let effectiveFilterKey = key;
    if (isAggregate) {
      if (key === 'sub_region') {
        effectiveFilterKey = 'region';
      } else if (Array.isArray(key) && key.includes('sub_region')) {
        effectiveFilterKey = key.map((k: string) =>
          k === 'sub_region' ? 'region' : k,
        );
      }
    }

    const config = {
      ...this.data,
      filterListener: sanitizedListener,
      filterListner: sanitizedListener,
      ...(effectiveFilterKey ? { filterKey: effectiveFilterKey } : {}),
    };

    // If no specific legend key is provided, we should ensure we don't send 'year'
    if (!key && config.filterKey) {
      const keys = Array.isArray(config.filterKey) ? config.filterKey : config.filterKey.split(',').map((k: string) => k.trim());
      const filteredKeys = keys.filter((k: string) => !['year', 'quarter', 'qtr', 'category'].includes(k));
      config.filterKey = filteredKeys;
    }

    return config;
  }

  /**
   * Override to also fade out labels on non-selected category bars.
   * ECharts per-datapoint label.opacity hides label text without removing the formatter.
   */
  protected override highlightCategories(categories: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const categoryIndices = categories
      .map((cat: string) => this.allCategories.indexOf(cat))
      .filter((i: number) => i !== -1);

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;

      const updatedData = s.data.map((val: any, i: number) => {
        const isSelected = categoryIndices.includes(i);
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          value: rawValue,
          itemStyle: { opacity: isSelected ? 1 : 0.4 },
          label: { opacity: isSelected ? 1 : 0 },
        };
      });

      return { ...s, data: updatedData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  /**
   * Override to restore label opacity when deselecting.
   */
  protected override resetCategoryHighlight(): void {
    this.selectedCategories = [];
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;

      const updatedData = s.data.map((val: any) => {
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          value: rawValue,
          itemStyle: { opacity: 1 },
          label: { opacity: 1 },
        };
      });

      return { ...s, data: updatedData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }





  /**
   * Resets local selection state (slices, years) and restores visual highlight.
   */
  public override reset(): void {
    super.reset();
    this.selectedSlices = [];
    this.selectedYears = [];
  }

  // ─── CSV EXPORT ───────────────────────────────────────────────────────────

  public downloadCsv(): void {
    const rawData = this.rawChartData;
    if (!rawData) return;

    const config = this.data?.chartConfig || {};
    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey || 'segment';
    const valueField = this.data?.valueField || 'value';
    const isQuarterly = this.enableQuarterYearAxis || config.enableQuarterYearAxis === true;

    if (dataKey && rawData[dataKey] && Array.isArray(rawData[dataKey])) {
      const rows: any[] = rawData[dataKey];
      if (isQuarterly) {
        const grandTotal = rows.reduce((s: number, r: any) => s + (Number(r[valueField]) || 0), 0);
        const headers = ['Year', 'Quarter', '%GT Sum', this.toHeaderLabel(seriesKey), 'Sum of Total Sales'];
        const csvRows = rows.map((r: any) => {
          const val = Number(r[valueField]) || 0;
          return [r['year'] ?? '', r['quarter'] ?? r['qtr'] ?? '', grandTotal > 0 ? Math.round((val / grandTotal) * 100) + '%' : '0%', r[seriesKey] ?? '', Math.round(val)];
        });
        this.triggerDownload(this.data?.widgetId || 'chart', headers, csvRows);
      } else {
        const headers = ['Year', this.toHeaderLabel(seriesKey), 'Sum of Total Sales'];
        const csvRows = rows.map((r: any) => [r['year'] ?? r['Year'] ?? '', r[seriesKey] ?? '', Math.round(Number(r[valueField]) || 0)]);
        this.triggerDownload(this.data?.widgetId || 'chart', headers, csvRows);
      }
      return;
    }

    if (rawData?.category && rawData?.series) {
      const categories: string[] = rawData.category;
      const seriesList: any[] = rawData.series;
      const grandTotal = seriesList.reduce((s: number, ser: any) =>
        s + (ser.data || []).reduce((ss: number, v: any) => ss + (Number(typeof v === 'object' ? v?.value : v) || 0), 0), 0);
      const extractYear = (s: string) => { const m = String(s).match(/(19|20)\d{2}/); return m ? m[0] : ''; };
      const extractQtr = (s: string) => { const m = String(s).toUpperCase().match(/\bQ\s*([1-4])\b/); return m ? `Q${m[1]}` : ''; };
      if (isQuarterly) {
        const headers = ['Year', 'Quarter', '%GT Sum', this.toHeaderLabel(seriesKey), 'Sum of Total Sales'];
        const csvRows: any[] = [];
        seriesList.forEach((ser: any) => {
          (ser.data || []).forEach((pt: any, i: number) => {
            const cat = categories[i] || '';
            const val = Number(typeof pt === 'object' ? pt?.value : pt) || 0;
            csvRows.push([extractYear(cat), extractQtr(cat), grandTotal > 0 ? Math.round((val / grandTotal) * 100) + '%' : '0%', ser.name, Math.round(val)]);
          });
        });
        this.triggerDownload(this.data?.widgetId || 'chart', headers, csvRows);
      } else {
        const headers = ['Year', this.toHeaderLabel(seriesKey), 'Sum of Total Sales'];
        const csvRows: any[] = [];
        seriesList.forEach((ser: any) => {
          (ser.data || []).forEach((pt: any, i: number) => {
            csvRows.push([categories[i] ?? '', ser.name, Math.round(Number(typeof pt === 'object' ? pt?.value : pt) || 0)]);
          });
        });
        this.triggerDownload(this.data?.widgetId || 'chart', headers, csvRows);
      }
    }
  }

  private toHeaderLabel(key: string): string {
    return (key || '').split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  private triggerDownload(widgetId: string, headers: string[], rows: any[][]): void {
    downloadAsExcel(widgetId, headers, rows);
  }
}


